/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';
import { Text, Title } from '@/shared/ui';

/**
 * Full-page loading placeholder for the public property view.
 */
export function PropertyLoadingState() {
  const { t } = useTranslation('@pages/public-property');
  return (
    <div className="flex min-h-screen items-center justify-center bg-(--surface-secondary) [font-family:var(--font-family)]">
      <div className="flex flex-col items-center gap-(--space-3)">
        <HousienBrandLogo variant="full" height={72} />
        <Text as="span" className="text-[length:var(--text-body-sm)] text-(--text-tertiary)">
          {t('loading')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Unavailable / 404 state for the public property view.
 */
export function PropertyUnavailableState() {
  const { t } = useTranslation('@pages/public-property');
  return (
    <div className="min-h-screen bg-(--surface-secondary) [font-family:var(--font-family)]">
      <header className="flex items-center border-b border-(--border-subtle) bg-(--surface-primary) px-(--space-5) lg:px-(--space-12)">
        <HousienBrandLogo variant="full" height={72} />
      </header>
      <div className="flex min-h-[calc(100vh-73px)] flex-col items-center justify-center gap-(--space-3) p-(--space-6)">
        <div className="flex size-12 items-center justify-center rounded-full bg-(--status-error)">
          <Text as="span" className="text-[length:var(--text-app-heading)] text-(--text-inverse)">
            !
          </Text>
        </div>
        <Title order={2} className="m-0 text-[length:var(--text-app-heading)] font-semibold text-(--text-primary)">
          {t('unavailable.title')}
        </Title>
        <Text className="m-0 max-w-[320px] text-center text-[length:var(--text-body-sm)] text-(--text-secondary)">
          {t('unavailable.description')}
        </Text>
      </div>
    </div>
  );
}
